import React, { useContext } from 'react';
import { Box, Divider, Paper, Typography } from '@mui/material';
import { gql, useQuery } from '@apollo/client';
import clientGraphql from '../../../common/apollo-graphql';
import AuthContext from '../../auth/context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const REFERRAL_LIST = gql`
  query ReferralList($startDate: String!, $endDate: String!, $type: String) {
    referral_list(start_date: $startDate, end_date: $endDate, type: $type) {
      name
      referrals
    }
  }
`;

const CustomTooltip = ({ active, payload, label, color }) => {
  if (!active || !payload?.length) return null;

  const { value } = payload[0];

  return (
    <Paper
      elevation={3}
      sx={{
        minWidth: 220,
        border: '2px solid #1abc9c',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box p={1}>
        {/* Header */}
        <Typography variant="subtitle2" sx={{ color: 'text.primary', textAlign: 'left' }}>
          {label}
        </Typography>

        <Divider sx={{ m: 1}}/>

        <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: color,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              Referrals
            </Typography>
          </Box>
          <Typography variant="body2" fontWeight="bold" color="text.primary">
            {value}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default function ReferralList({ startDate, endDate, type }) {
  const currentUser = useContext(AuthContext);
  const color = type === 'sent' ? '#1abc9c' : '#3498db';

  const { data, loading } = useQuery(REFERRAL_LIST, {
    client: clientGraphql,
    variables: {
      type,
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !currentUser,
  });

  const referrals = (data?.referral_list || []);

  const totalReferrals = referrals.reduce((sum, r) => sum + r.referrals, 0);

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="h4" gutterBottom>
        Showing data from {startDate.format('MMMM YYYY')} - {endDate.format('MMMM YYYY')}<br />
      </Typography>

      {!loading && referrals.length > 0 ? (
        <>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              mb: 2,
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                backgroundColor: color,
              }}
            />
            <Typography variant="h6" color="text.primary" sx={{fontSize: 20}}>
              Referrals ({totalReferrals})
            </Typography>
          </Box>

          <Box sx={{ height: Math.max(referrals.length * 40, 500), width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={referrals} layout="vertical">
                <XAxis type="number" domain={[0, 'dataMax']} allowDecimals={false}/>
                <YAxis
                  type="category"
                  width={200}
                  dataKey="name"
                  tick={({ x, y, payload }) => {
                    return (
                      <foreignObject x={x - 190} y={y - 10} width={180} height={20}>
                        <div
                          xmlns="http://www.w3.org/1999/xhtml"
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                            fontSize: '12px',
                            textAlign: 'right',
                            lineHeight: '20px',
                          }}
                          title={payload.value}
                        >
                          {payload.value}
                        </div>
                      </foreignObject>
                    );
                  }}
                />
                <Tooltip content={<CustomTooltip color={color} />} />
                <Bar
                  dataKey="referrals"
                  fill={color}
                  isAnimationActive={false}
                  activeBar={false}
                  onMouseOver={() => { }}
                  onMouseLeave={() => { }}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </>
      ) : (
        <Typography>No data found</Typography>
      )}
    </Box>
  );
}
